import type { ReactElement } from "react";
import type React from "react";

export type IconName =
  | "home"
  | "users"
  | "table"
  | "list"
  | "coin"
  | "globe"
  | "bed"
  | "sparkle"
  | "plus"
  | "search"
  | "bell"
  | "chevron"
  | "check"
  | "x"
  | "drag"
  | "calendar"
  | "map"
  | "image"
  | "image2"
  | "chat"
  | "setting"
  | "arrowRight"
  | "arrowDown"
  | "arrowUp"
  | "file"
  | "palette"
  | "clock"
  | "heart"
  | "pin"
  | "info"
  | "qr"
  | "menu"
  | "bookmark"
  | "trash"
  | "paperclip"
  | "mic"
  | "rings"
  | "send"
  | "unpair"
  | "logOut"
  | "fitView"
  | "papeteria";

interface IconProps {
  name: IconName;
  size?: number;
  stroke?: number;
  className?: string;
  style?: React.CSSProperties;
  "aria-label"?: string;
  title?: string;
}

const PATHS: Record<IconName, ReactElement> = {
  home: <path d="M3 11l9-7 9 7v9a1 1 0 01-1 1h-5v-6h-6v6H4a1 1 0 01-1-1z" />,
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 19c.6-3.3 3.4-5 6.5-5s5.9 1.7 6.5 5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M21.5 17c-.4-2-2-3-4-3" />
    </>
  ),
  table: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M3 12h18M9 6v12M15 6v12" />
    </>
  ),
  list: <path d="M4 6h16M4 12h16M4 18h10" />,
  coin: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9.5C9 8 10.3 7 12 7s3 1 3 2.5-1 2-3 2.5-3 1-3 2.5S10.3 17 12 17s3-1 3-2.5" />
      <path d="M12 5.5v13" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
    </>
  ),
  bed: <path d="M3 18v-7a3 3 0 013-3h12a3 3 0 013 3v7M3 14h18M7 11h3v-1H7z" />,
  sparkle: <path d="M12 3v6M12 15v6M3 12h6M15 12h6M6 6l3 3M15 15l3 3M18 6l-3 3M6 18l3-3" />,
  plus: <path d="M12 5v14M5 12h14" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </>
  ),
  bell: <path d="M6 17V11a6 6 0 1112 0v6M4 17h16M10 21h4" />,
  chevron: <path d="M9 6l6 6-6 6" />,
  check: <path d="M5 12l4 4 10-10" />,
  x: <path d="M6 6l12 12M6 18L18 6" />,
  drag: (
    <>
      <circle cx="9" cy="6" r="1" />
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="18" r="1" />
      <circle cx="15" cy="6" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="18" r="1" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  map: (
    <>
      <path d="M9 4l6 2 6-2v14l-6 2-6-2-6 2V6z" />
      <path d="M9 4v16M15 6v16" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="M3 17l5-4 4 3 3-2 6 5" />
    </>
  ),
  image2: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="1.5" />
      <path d="M21 16l-5-4-3 3-2-2-8 7" />
    </>
  ),
  chat: (
    <>
      <path d="M21 12a8 8 0 11-16 0 8 8 0 0116 0z" />
      <path d="M5 18l-2 3 5-1" />
    </>
  ),
  setting: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.6 1.6 0 00-1.8-.3 1.6 1.6 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.6 1.6 0 00-1-1.5 1.6 1.6 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.6 1.6 0 00.3-1.8 1.6 1.6 0 00-1.5-1H3a2 2 0 110-4h.1a1.6 1.6 0 001.5-1 1.6 1.6 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.6 1.6 0 001.8.3H9a1.6 1.6 0 001-1.5V3a2 2 0 114 0v.1a1.6 1.6 0 001 1.5 1.6 1.6 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.6 1.6 0 00-.3 1.8V9a1.6 1.6 0 001.5 1H21a2 2 0 110 4h-.1a1.6 1.6 0 00-1.5 1z" />
    </>
  ),
  arrowRight: <path d="M5 12h14M13 6l6 6-6 6" />,
  arrowDown: <path d="M12 5v14M6 13l6 6 6-6" />,
  arrowUp: <path d="M12 19V5M6 11l6-6 6 6" />,
  file: (
    <>
      <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
      <path d="M14 3v5h5" />
    </>
  ),
  palette: (
    <>
      <path d="M12 3a9 9 0 100 18c1 0 1.5-.7 1.5-1.5 0-.5-.3-.9-.6-1.2-.4-.4-.6-.9-.6-1.5 0-1 .8-1.8 1.8-1.8H16a5 5 0 005-5c0-4-4-7-9-7z" />
      <circle cx="7" cy="11" r="1" />
      <circle cx="9" cy="7" r="1" />
      <circle cx="13" cy="6" r="1" />
      <circle cx="17" cy="9" r="1" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  heart: <path d="M12 20s-7-4.5-7-10a4 4 0 017-2.6A4 4 0 0119 10c0 5.5-7 10-7 10z" />,
  pin: (
    <>
      <path d="M12 21s-7-7-7-12a7 7 0 0114 0c0 5-7 12-7 12z" />
      <circle cx="12" cy="9" r="2.5" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v5h1" />
    </>
  ),
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3M21 14v7M14 17v4h7" />
    </>
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  bookmark: (
    <>
      <path d="M6 4h12a1 1 0 011 1v15l-7-4-7 4V5a1 1 0 011-1z" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1-2h10l1 2M9 7V5h6v2" />
    </>
  ),
  paperclip: <path d="M21 12l-9 9a5 5 0 01-7-7l9-9a3 3 0 014 4l-9 9a1 1 0 01-1-1l7-7" />,
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0014 0M12 18v3M9 21h6" />
    </>
  ),
  rings: (
    <>
      <circle cx="9.5" cy="13" r="4.5" />
      <circle cx="14.5" cy="13" r="4.5" />
    </>
  ),
  send: (
    <>
      <path d="M22 2L11 13" />
      <path d="M22 2L15 22l-4-9-9-4 20-7z" />
    </>
  ),
  unpair: (
    <>
      <path d="M15.5 8.5l-2-2a3.5 3.5 0 00-5 5l2 2" />
      <path d="M8.5 15.5l2 2a3.5 3.5 0 005-5l-2-2" />
      <path d="M4 4l16 16" />
    </>
  ),
  logOut: (
    <>
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </>
  ),
  fitView: (
    <>
      <path d="M8 3H5a2 2 0 00-2 2v3M16 3h3a2 2 0 012 2v3M21 16v3a2 2 0 01-2 2h-3M8 21H5a2 2 0 01-2-2v-3" />
    </>
  ),
  papeteria: (
    <>
      <rect x="6" y="3" width="13" height="17" rx="1.5" transform="rotate(-5 12 11)" />
      <rect x="5" y="5" width="13" height="17" rx="1.5" transform="rotate(4 12 13)" />
      <path d="M9 12h7M9 15h5" transform="rotate(4 12 13)" />
    </>
  ),
};

export function Icon({
  name,
  size = 16,
  stroke = 1.5,
  className,
  style,
  "aria-label": ariaLabel,
  title,
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      role={ariaLabel ? "img" : undefined}
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}
